/**
 * App Bridge v4 CDN type shim.
 *
 * The CDN script attaches a `shopify` object to `window`. We only call
 * `idToken()` (for our authenticated fetch) and `toast.show()` (for quick
 * feedback). Keep this surface minimal — Polaris handles most UI.
 *
 * Ref: https://shopify.dev/docs/api/app-bridge-library/reference
 */
export interface ShopifyAppBridge {
  idToken(): Promise<string>;
  toast: {
    show(message: string, options?: { duration?: number; isError?: boolean }): void;
  };
  config: {
    shop?: string;
    host?: string;
    apiKey: string;
  };
}

declare global {
  interface Window {
    shopify?: ShopifyAppBridge;
  }
}

/**
 * Resolve the global once. The CDN sets it synchronously, but during Vite HMR
 * or pre-mount ticks it may briefly be undefined — callers should await.
 */
export async function getAppBridge(): Promise<ShopifyAppBridge> {
  if (window.shopify) return window.shopify;
  // Poll briefly (max ~1s) for the CDN to boot. Longer than that means the
  // script failed to load and we surface an error so the banner shows up.
  for (let i = 0; i < 20; i++) {
    await new Promise((r) => setTimeout(r, 50));
    if (window.shopify) return window.shopify;
  }
  throw new Error('app_bridge_not_loaded');
}
